// Lap + sector timing. Detect S/F crossings (using the wall-extended
// trigger zone, not just the visible portion), accumulate lap times,
// surface best/last/live to the HUD.
//
// Lap validity: a forward S/F crossing only counts as a completed lap
// if the car has passed through every authored sector boundary in order
// since the previous S/F crossing. This stops "U-turn at S/F to spam
// laps" — the car has to actually go around the circuit. Tracks without
// sector boundaries skip this check.
//
// `now` is sim-time in milliseconds, not wall-clock. The simulator
// loop passes `world.simTime * 1000`; this keeps lap timing in
// lockstep with the physics so a paused or tab-throttled sim doesn't
// inflate live lap times, and replays produce identical timing.

import { segmentIntersect } from '../../shared/geometry.js';

export function resetLapState(lapState) {
  lapState.prevPos            = null;
  lapState.lapNumber          = 0;
  lapState.currentLapStart    = null;
  lapState.lastLapTime        = null;
  lapState.bestLapTime        = null;
  lapState.currentSector      = 0;
  lapState.currentSectorStart = null;
  // Two parallel sector-time arrays:
  //   currentLapSectorTimes — the in-progress lap. Reset to [null,null,null]
  //     at every lap start and filled as each sector gate is crossed.
  //   lastSectorTimes — the most recently completed full lap, frozen at
  //     the S/F-rollover instant. Acts as the display fallback so the
  //     panel keeps showing prior-lap sectors until the current lap
  //     overwrites them one-by-one (F1 timing convention).
  lapState.currentLapSectorTimes = [null, null, null];
  lapState.lastSectorTimes       = [null, null, null];
  lapState.bestSectorTimes       = [null, null, null];
  lapState.currentLapStandingStart = false;
  // Absolute sim-time of each car's most recent forward crossing of
  // each timing gate. Used by the T&S panel to compute a stable
  // gap-to-ahead that updates at every gate (not just S/F) without
  // rubberbanding from instantaneous arc differences. Three slots:
  // sf (start/finish), s0 (sector0 boundary), s1 (sector1 boundary).
  lapState.lastGateTimes = { sf: null, s0: null, s1: null };
}

// How many sector gates the car must cross for a lap to be valid.
// We require BOTH or NEITHER — a lone gate without its partner can't
// form a meaningful in-order check. Returns 0 (no validation, any S/F
// crossing counts) or 2 (full 3-sector layout).
export function lapValidationMaxStage(timingLines) {
  return (timingLines?.sector0 && timingLines?.sector1) ? 2 : 0;
}

// Each tick: see if the car's motion vector has crossed S/F or any
// sector gate in the forward direction. Crossing detection uses the
// WALL-EXTENDED trigger zones so cutting across the grass off-track
// still counts (or doesn't, in the case of out-of-order sector gates).
export function updateLapTiming(lapState, car, timingLines, now) {
  if (!timingLines || !timingLines.startFinish) return;

  if (!lapState.prevPos) {
    lapState.prevPos = { x: car.pos.x, y: car.pos.y };
    return;
  }

  const p0 = lapState.prevPos;
  const p1 = car.pos;

  // Out-lap → racing-lap-1 transition at the start line, when one is
  // authored. Tracks with a separate startLine (e.g., grid is offset
  // from S/F) want lap 1's clock to begin when the leader crosses
  // the start line, not when they later cross S/F. Subsequent laps
  // ignore this marker (the gate clause `lapNumber === 0 && start
  // == null` only fires once). When no startLine is authored, the
  // first S/F crossing transitions out-lap → lap 1 (in
  // onCrossStartFinish), preserving the original behavior.
  if (lapState.lapNumber === 0 && lapState.currentLapStart == null
      && timingLines.startLine
      && segmentsCrossForward(p0, p1, timingLines.startLine)) {
    onCrossStartLine(lapState, now);
  }

  // S/F first — handles both first-ever crossing and lap completion.
  if (segmentsCrossForward(p0, p1, timingLines.startFinish)) {
    onCrossStartFinish(lapState, timingLines, now);
  }

  // Pit-lane S/F (when authored). Same lap-counter effect as the main
  // S/F crossing, but skips sector validation — cars on the pit lane
  // bypass the racing-surface sector gates by definition. The lap
  // counter increments and the timer resets, but no time is recorded
  // for the previous lap (the lap was via pit, not a flying lap).
  if (timingLines.pitStartFinish &&
      segmentsCrossForward(p0, p1, timingLines.pitStartFinish)) {
    onCrossPitStartFinish(lapState, now);
  }

  // Sector progression. Only the next-expected gate advances the
  // counter; out-of-order or already-passed crossings are silently
  // dropped so cutting the course can't fast-forward gates.
  if (lapState.currentSector === 0 && timingLines.sector0
      && segmentsCrossForward(p0, p1, timingLines.sector0)) {
    onCrossSector(lapState, 0, now);
  } else if (lapState.currentSector === 1 && timingLines.sector1
      && segmentsCrossForward(p0, p1, timingLines.sector1)) {
    onCrossSector(lapState, 1, now);
  }

  lapState.prevPos = { x: p1.x, y: p1.y };
}

// Did the segment p0→p1 cross the timing line `tline`, and was the
// motion in the same direction as the line's track tangent?
function segmentsCrossForward(p0, p1, tline) {
  const hit = segmentIntersect(p0, p1, tline.a, tline.b);
  if (!hit) return false;
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  return (dx * tline.tangent.x + dy * tline.tangent.y) > 0;
}

// Out-lap → racing-lap-1 transition triggered by the optional
// startLine marker. Sets currentLapStart so the eventual S/F
// crossing closes lap 1 (instead of starting it). Sector timing
// also begins here, so sector 1 of lap 1 = startLine → sector0.
// Note: if startLine is positioned past one or more sector
// markers in track order, those sectors won't get times for lap 1
// (their gates were "behind" the car at start). The lap still
// counts.
//
// Marks the lap as a "standing start" so its time is recorded but
// excluded from bestLapTime / best sector times — the lap is
// shorter than a full S/F-to-S/F lap, so isn't a fair comparator
// for fastest-lap awards. This matches IndyCar/F1 timing where
// "Fastest Lap of the Race" is computed only over laps 2+ on
// tracks with a separate start line.
function onCrossStartLine(lapState, now) {
  lapState.lapNumber          = 1;
  lapState.currentLapStart    = now;
  lapState.currentSectorStart = now;
  lapState.currentSector      = 0;
  lapState.currentLapStandingStart = true;
  // Reset the in-progress sector tuple; lastSectorTimes is left
  // untouched (no completed lap to record on an out-lap → lap 1).
  lapState.currentLapSectorTimes = [null, null, null];
  // The startLine acts as the lap-1 anchor for stable-gap math.
  lapState.lastGateTimes.sf = now;
}

function onCrossSector(lapState, sectorIdx, now) {
  // sectorIdx 0 = closing sector S1; 1 = closing sector S2.
  if (lapState.currentSectorStart != null) {
    recordSectorTime(lapState, sectorIdx, now - lapState.currentSectorStart);
  }
  lapState.currentSector      = sectorIdx + 1;
  lapState.currentSectorStart = now;
  // Stamp the gate's absolute crossing time for stable-gap math.
  if (sectorIdx === 0) lapState.lastGateTimes.s0 = now;
  else if (sectorIdx === 1) lapState.lastGateTimes.s1 = now;
}

function recordSectorTime(lapState, idx, time) {
  // Writes into the in-progress lap. lastSectorTimes is updated as a
  // single atomic snapshot when the lap closes (see onCrossStartFinish).
  lapState.currentLapSectorTimes[idx] = time;
  // Standing-start lap: record the time so the user sees it, but
  // don't compare against bests. S1 of a standing-start lap is the
  // truncated startLine → sector0 chunk and would unfairly hold the
  // sector record forever. S2 / S3 are nominally full sectors but
  // the car enters S2 from a non-standard S1, so skip them too —
  // simpler and more conservative.
  if (lapState.currentLapStandingStart) return;
  if (lapState.bestSectorTimes[idx] == null || time < lapState.bestSectorTimes[idx]) {
    lapState.bestSectorTimes[idx] = time;
  }
}

function onCrossPitStartFinish(lapState, now) {
  const onOutLap = lapState.currentLapStart == null;
  if (onOutLap) {
    lapState.lapNumber          = 1;
    lapState.currentLapStart    = now;
    lapState.currentSectorStart = now;
    lapState.currentSector      = 0;
    lapState.currentLapSectorTimes = [null, null, null];
    lapState.lastGateTimes.sf = now;
    return;
  }
  // Pit-out crossing. Increment lap counter, reset the lap + sector
  // timers. Don't record a time for the previous lap — the car
  // skipped the racing surface, so its previous-lap time is invalid.
  // Pit-skipped lap also doesn't snapshot into lastSectorTimes; the
  // previous lap's frozen sectors stay as the fallback display.
  lapState.lapNumber++;
  lapState.currentLapStart    = now;
  lapState.currentSectorStart = now;
  lapState.currentSector      = 0;
  lapState.currentLapSectorTimes = [null, null, null];
  // Pit lap bypassed s0/s1 — clear them so a stale stamp from a
  // pre-pit lap doesn't beat sf in the gate-pick logic.
  lapState.lastGateTimes.sf = now;
  lapState.lastGateTimes.s0 = null;
  lapState.lastGateTimes.s1 = null;
}

function onCrossStartFinish(lapState, timingLines, now) {
  const maxStage = lapValidationMaxStage(timingLines);
  const onOutLap = lapState.currentLapStart == null;

  if (onOutLap) {
    // First-ever forward S/F crossing — start lap 1. Nothing to record.
    lapState.lapNumber          = 1;
    lapState.currentLapStart    = now;
    lapState.currentSectorStart = now;
    lapState.currentSector      = 0;
    lapState.currentLapSectorTimes = [null, null, null];
    lapState.lastGateTimes.sf = now;
    return;
  }

  if (lapState.currentSector < maxStage) {
    // Crossed S/F without driving the actual circuit (U-turn, cut).
    // Don't count it; don't reset the timer — the live time keeps
    // growing so the user notices something's off.
    return;
  }

  // Real lap completion. Close out the final sector first (only if the
  // track has sectors authored) — for a 3-sector layout that's S3,
  // recorded at index `maxStage` (= 2).
  if (maxStage > 0 && lapState.currentSectorStart != null) {
    recordSectorTime(lapState, maxStage, now - lapState.currentSectorStart);
  }
  const lapTime = now - lapState.currentLapStart;
  lapState.lastLapTime = lapTime;
  // Standing-start lap (lap 1 with a separate startLine) doesn't count
  // toward best — see onCrossStartLine for rationale.
  if (!lapState.currentLapStandingStart) {
    if (lapState.bestLapTime == null || lapTime < lapState.bestLapTime) {
      lapState.bestLapTime = lapTime;
    }
  }
  lapState.currentLapStandingStart = false;   // subsequent laps are full
  lapState.lapNumber++;
  lapState.currentLapStart    = now;
  lapState.currentSectorStart = now;
  lapState.currentSector      = 0;
  lapState.lastGateTimes.sf   = now;
  // Snapshot the just-finished lap's sectors as the new "last lap"
  // tuple, then clear the in-progress array so the next lap starts
  // empty. The display layer falls back from currentLapSectorTimes
  // to lastSectorTimes per cell (F1 timing convention) — so S3 of
  // the lap that just closed remains visible as the fallback until
  // the next lap reaches sector0 / sector1 / S/F respectively.
  lapState.lastSectorTimes       = [...lapState.currentLapSectorTimes];
  lapState.currentLapSectorTimes = [null, null, null];
}

export function fmtLap(ms) {
  if (ms == null) return '—:——.———';
  const totalSec = ms / 1000;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec - min * 60;
  return `${min}:${sec.toFixed(3).padStart(6, '0')}`;
}

export function fmtSector(ms) {
  if (ms == null) return '——.———';
  return (ms / 1000).toFixed(3);
}
